<template>
  <div class="dashboard-settings relative">
    <!-- Settings Button (now inline instead of fixed) -->
    <button
      ref="buttonRef"
      @click.stop="toggleSettings"
      class="btn p-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-blue-400"
      title="Dashboard Settings"
    >
      <Settings class="h-5 w-5" />
    </button>



    <!-- Settings Dropdown with Backdrop -->
    <teleport to="body">
      <!-- Backdrop overlay -->
      <transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showSettings"
          @click="toggleSettings"
          class="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998]"
        ></div>
      </transition>

      <!-- Settings Dropdown (fixed positioning above everything) -->
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="showSettings"
          @click.stop
          ref="dropdown"
          class="fixed w-72 glass-card rounded-xl shadow-2xl p-4 z-[9999]"
          :style="dropdownStyle"
        >
          <h3 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Dashboard Sections</h3>
          <div class="space-y-3">
            <label
              v-for="section in sections"
              :key="section.id"
              class="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <input
                type="checkbox"
                :checked="modelValue[section.id]"
                @change="updateSection(section.id)"
                class="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary h-5 w-5 cursor-pointer"
              />
              <span class="text-gray-700 dark:text-gray-200 font-medium">{{ section.label }}</span>
            </label>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Settings } from "lucide-vue-next";
import Cookies from "js-cookie";

const COOKIE_NAME = "dashboard-preferences";
const HINT_COOKIE_NAME = "dashboard-hint-dismissed";
const COOKIE_EXPIRY = 365;

export default {
  name: "DashboardSelector",
  components: { Settings },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    route: {
      type: String,
      default: ''
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const showSettings = ref(false);
    const showHint = ref(false);
    const dropdown = ref(null);
    const buttonRef = ref(null);

    const dropdownStyle = computed(() => {
      if (!buttonRef.value) return {};
      const rect = buttonRef.value.getBoundingClientRect();
      return {
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
    });
    
    const sections = [
      { id: "showStat", label: "Show Graphs" },
      { id: "changes", label: "Pending Changes" },
      { id: "infrastructure", label: "Infrastructure Status" },
      { id: "sites", label: "Site Overview" },
      { id: "probes", label: "Probe Overview" },
      { id: "tunnels", label: "Tunnel Overview" },
      { id: "divisions", label: "Division Overview" },
      { id: "security", label: "Security Overview" },
      { id: "activity", label: "Recent Activity" },
      { id: "apiAudit", label: "System Activity" },
    ];

    const handleClickOutside = (event) => {
      const settingsBox = document.querySelector(".settings-box");
      const settingsButton = document.querySelector(".settings-button");
      if (
        !event.target.closest(".settings-box") &&
        !event.target.closest(".settings-button") &&
        showSettings.value
      ) {
        showSettings.value = false;
      }
    };

    // Dismiss hint and save preference to cookie
    const dismissHint = () => {
      showHint.value = false;
      Cookies.set(HINT_COOKIE_NAME, 'true', { expires: COOKIE_EXPIRY });
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      
      // Load dashboard preferences
      const savedPrefs = Cookies.get(COOKIE_NAME);
      if (savedPrefs) {
        try {
          emit("update:modelValue", JSON.parse(savedPrefs));
        } catch (e) {
          console.error("Failed to parse dashboard preferences:", e);
        }
      }
      
      // Show hint if not previously dismissed
      const hintDismissed = Cookies.get(HINT_COOKIE_NAME);
      if (!hintDismissed) {
        // Only show hint if we're on the dashboard page
        if (props.route === 'dashboard' || window.location.pathname === '/' || window.location.pathname.includes('dashboard')) {
          // Slight delay for better UX
          setTimeout(() => {
            showHint.value = true;
          }, 1000);
        }
      }
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    watch(
      () => props.modelValue,
      (newValue) => {
        Cookies.set(COOKIE_NAME, JSON.stringify(newValue), {
          expires: COOKIE_EXPIRY,
        });
      },
      { deep: true }
    );

    const toggleSettings = () => {
      showSettings.value = !showSettings.value;
      // Hide hint when settings are opened
      if (showSettings.value) {
        showHint.value = false;
      }
    };

    const updateSection = (sectionId) => {
      const newValue = { ...props.modelValue };
      newValue[sectionId] = !newValue[sectionId];
      emit("update:modelValue", newValue);
    };

    return {
      showSettings,
      showHint,
      dropdown,
      buttonRef,
      dropdownStyle,
      sections,
      updateSection,
      toggleSettings,
      dismissHint,
    };
  },
};
</script>

<style scoped>
.hint-box {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
